import React, { useEffect, useState, useRef } from 'react';
import { motion, AnimatePresence, useReducedMotion, useInView } from 'framer-motion';
import { CheckIcon, FileIcon, SparklesIcon } from 'lucide-react';
const attachments = [{
  name: 'Attachment 1.pdf',
  size: '13 KB',
  mapped: 'TRIA Form'
}, {
  name: 'Attachment 2.pdf',
  size: '50 KB',
  mapped: 'ACORD 125 & 131'
}, {
  name: 'Attachment 3.docx',
  size: '109 KB',
  mapped: 'Underlying GL Policy'
}, {
  name: 'Attachment 4.xlsx',
  size: '43 KB',
  mapped: 'Loss Runs'
}, {
  name: 'Attachment 5.pdf',
  size: '20 KB',
  mapped: 'Underlying GL Policy'
}, {
  name: 'Attachment 6.pdf',
  size: '35 KB',
  mapped: 'Loss Runs'
}];
const checklist = [{
  label: 'ACORD 125 & 131',
  short: '1. ACORD Applications (125 & 131)',
  mobileShort: '1. ACORD 125 & 131'
}, {
  label: 'TRIA Form',
  short: '2. TRIA Form',
  mobileShort: '2. TRIA Form'
}, {
  label: 'Underlying GL Policy',
  short: '3. Underlying General Liability Policy',
  mobileShort: '3. Underlying GL Policy'
}, {
  label: 'Loss Runs',
  short: '4. Loss Runs',
  mobileShort: '4. Loss Runs'
}];
const STEP = 1300;
const PULSE_DURATION = 750;
const START_DELAY = 700;
const HOLD_AFTER_COMPLETE = 2400;
type WirePath = {
  d: string;
  sourceIdx: number;
  destIdx: number;
};
export function AttachmentIngestionMock() {
  const [processedDestIdx, setProcessedDestIdx] = useState(0);
  const [activeDestIdx, setActiveDestIdx] = useState<number>(-1);
  const rootRef = useRef<HTMLDivElement>(null);
  const reduced = useReducedMotion();
  const inView = useInView(rootRef, {
    amount: 0.25
  });
  const sourceRefs = useRef<(HTMLLIElement | null)[]>([]);
  const destRefs = useRef<(HTMLLIElement | null)[]>([]);
  const wiresRef = useRef<HTMLDivElement>(null);
  const [paths, setPaths] = useState<WirePath[]>([]);
  useEffect(() => {
    if (reduced) {
      setProcessedDestIdx(checklist.length);
      setActiveDestIdx(-1);
      return;
    }
    if (!inView) return;
    const timeouts: ReturnType<typeof setTimeout>[] = [];
    const run = () => {
      setProcessedDestIdx(0);
      setActiveDestIdx(-1);
      checklist.forEach((_, i) => {
        timeouts.push(setTimeout(() => setActiveDestIdx(i), START_DELAY + i * STEP));
        timeouts.push(setTimeout(() => {
          setProcessedDestIdx(i + 1);
          setActiveDestIdx(-1);
        }, START_DELAY + i * STEP + PULSE_DURATION));
      });
      timeouts.push(setTimeout(run, START_DELAY + checklist.length * STEP + HOLD_AFTER_COMPLETE));
    };
    run();
    return () => timeouts.forEach(clearTimeout);
  }, [reduced, inView]);
  // Compute curved bezier wires between each attachment row and its destination row.
  // Recomputed on resize so layout changes (mobile/desktop, font load, etc.) are tracked.
  useEffect(() => {
    const compute = () => {
      const wrap = wiresRef.current;
      if (!wrap) return;
      const wrapRect = wrap.getBoundingClientRect();
      const next: WirePath[] = [];
      attachments.forEach((a, srcIdx) => {
        const destIdx = checklist.findIndex(c => c.label === a.mapped);
        const src = sourceRefs.current[srcIdx];
        const dst = destRefs.current[destIdx];
        if (!src || !dst || destIdx < 0) return;
        const sr = src.getBoundingClientRect();
        const dr = dst.getBoundingClientRect();
        const sx = sr.right - wrapRect.left;
        const sy = sr.top + sr.height / 2 - wrapRect.top;
        const dx = dr.left - wrapRect.left;
        const dy = dr.top + dr.height / 2 - wrapRect.top;
        const cp = Math.max(24, (dx - sx) * 0.55);
        const d = `M ${sx},${sy} C ${sx + cp},${sy} ${dx - cp},${dy} ${dx},${dy}`;
        next.push({
          d,
          sourceIdx: srcIdx,
          destIdx
        });
      });
      setPaths(next);
    };
    compute();
    const ro = new ResizeObserver(compute);
    if (wiresRef.current) ro.observe(wiresRef.current);
    window.addEventListener('resize', compute);
    return () => {
      ro.disconnect();
      window.removeEventListener('resize', compute);
    };
  }, []);
  const activeMapping = activeDestIdx >= 0 ? checklist[activeDestIdx].label : null;
  const completeCount = processedDestIdx;
  const isAllDone = processedDestIdx === checklist.length;
  return <div ref={rootRef} className="relative rounded-2xl border border-white/10 bg-zinc-900/80 p-4 shadow-2xl shadow-black/40 sm:p-5" data-id="element-34">
      {/* Header */}
      <div className="mb-4 flex items-center justify-between" data-id="element-35">
        <div className="flex items-center gap-2" data-id="element-36">
          <SparklesIcon className="h-4 w-4 text-accent" data-id="element-37" />
          <span className="flex items-center gap-2 text-[11px] font-medium uppercase tracking-wider text-accent sm:text-xs" data-id="element-38">
            Pigeon AI · {isAllDone ? 'Complete' : 'Categorizing'}
            <AnimatePresence mode="wait" data-id="element-39">
              {!isAllDone ? <motion.span key="working" initial={{
              opacity: 0,
              scale: 0.6
            }} animate={{
              opacity: 1,
              scale: 1
            }} exit={{
              opacity: 0,
              scale: 0.6
            }} transition={{
              duration: 0.2
            }} className="inline-flex" data-id="element-40">
                  <motion.span animate={{
                rotate: 360
              }} transition={{
                duration: 1.2,
                repeat: Infinity,
                ease: 'linear'
              }} className="block h-2.5 w-2.5 rounded-full border border-accent/30 border-t-accent" data-id="element-41" />
                </motion.span> : <motion.span key="done" initial={{
              opacity: 0,
              scale: 0.6
            }} animate={{
              opacity: 1,
              scale: 1
            }} exit={{
              opacity: 0,
              scale: 0.6
            }} transition={{
              duration: 0.25,
              ease: 'backOut'
            }} className="flex h-2.5 w-2.5 items-center justify-center rounded-full bg-accent" data-id="element-42">
                  <CheckIcon className="h-1.5 w-1.5 text-zinc-950" strokeWidth={4} data-id="element-43" />
                </motion.span>}
            </AnimatePresence>
          </span>
        </div>
        <div className="rounded-md border border-emerald-500/20 bg-emerald-500/10 px-2 py-1 font-mono text-[10px] tabular-nums text-emerald-400" data-id="element-44">
          <AnimatePresence mode="popLayout" data-id="element-45">
            <motion.span key={completeCount} initial={{
            opacity: 0,
            y: -3
          }} animate={{
            opacity: 1,
            y: 0
          }} exit={{
            opacity: 0,
            y: 3
          }} transition={{
            duration: 0.2
          }} className="inline-block" data-id="element-46">
              {completeCount} / {checklist.length} Complete
            </motion.span>
          </AnimatePresence>
        </div>
      </div>

      {/* MOBILE: single-column checklist where attachments fly into their matching row */}
      <div className="sm:hidden" data-id="element-47">
        <div className="mb-3 text-[10px] font-medium uppercase tracking-wider text-zinc-500" data-id="element-48">
          Request Checklist
        </div>
        <ul className="flex flex-col gap-2" data-id="element-49">
          {checklist.map((c, i) => {
          const matchedAttachments = attachments.filter(a => a.mapped === c.label);
          const totalForDest = matchedAttachments.length;
          const isFilled = i < processedDestIdx;
          const isReceiving = i === activeDestIdx;
          return <motion.li key={c.label} animate={{
            borderColor: isFilled ? 'rgba(16,185,129,0.30)' : isReceiving ? 'rgba(56,189,248,0.55)' : 'rgba(255,255,255,0.05)',
            backgroundColor: isFilled ? 'rgba(16,185,129,0.05)' : isReceiving ? 'rgba(56,189,248,0.08)' : 'rgba(24,24,27,0.5)'
          }} transition={{
            duration: 0.3
          }} className="relative overflow-hidden rounded-lg border px-3 py-2.5" data-id="element-50">
                {isReceiving && <motion.div aria-hidden animate={{
              opacity: [0, 0.5, 0]
            }} transition={{
              duration: 1,
              repeat: Infinity
            }} className="pointer-events-none absolute inset-0 rounded-lg ring-1 ring-accent/50" data-id="element-51" />}

                <div className="relative flex items-center gap-2.5" data-id="element-52">
                  {/* Status indicator */}
                  <div className={`flex h-5 w-5 flex-shrink-0 items-center justify-center rounded-full transition-colors ${isFilled ? 'bg-emerald-500/20' : isReceiving ? 'bg-accent/15' : 'border border-white/10 bg-white/[0.03]'}`} data-id="element-53">
                    <AnimatePresence mode="wait" data-id="element-54">
                      {isFilled ? <motion.div key="check" initial={{
                    scale: 0,
                    opacity: 0
                  }} animate={{
                    scale: 1,
                    opacity: 1
                  }} exit={{
                    scale: 0,
                    opacity: 0
                  }} transition={{
                    duration: 0.3,
                    ease: 'backOut'
                  }} data-id="element-55">
                          <CheckIcon className="h-3 w-3 text-emerald-400" strokeWidth={3} data-id="element-56" />
                        </motion.div> : isReceiving ? <motion.div key="spinner" initial={{
                    opacity: 0
                  }} animate={{
                    opacity: 1
                  }} exit={{
                    opacity: 0
                  }} transition={{
                    duration: 0.15
                  }} data-id="element-57">
                          <motion.div animate={{
                      rotate: 360
                    }} transition={{
                      duration: 1.2,
                      repeat: Infinity,
                      ease: 'linear'
                    }} className="h-3 w-3 rounded-full border-2 border-accent/20 border-t-accent" data-id="element-58" />
                        </motion.div> : null}
                    </AnimatePresence>
                  </div>

                  {/* Label + status text */}
                  <div className="min-w-0 flex-1" data-id="element-59">
                    <div className={`truncate text-[12.5px] font-medium leading-tight transition-colors ${isFilled || isReceiving ? 'text-zinc-200' : 'text-zinc-500'}`} data-id="element-60">
                      {c.mobileShort}
                    </div>
                    <div className="mt-0.5 font-mono text-[9px] uppercase tracking-[0.14em]" data-id="element-61">
                      <AnimatePresence mode="wait" data-id="element-62">
                        {isFilled ? <motion.span key="filed" initial={{
                      opacity: 0,
                      y: 2
                    }} animate={{
                      opacity: 1,
                      y: 0
                    }} exit={{
                      opacity: 0
                    }} transition={{
                      duration: 0.2
                    }} className="text-emerald-400" data-id="element-63">
                            {totalForDest > 1 ? `${totalForDest} files filed` : '1 file filed'}
                          </motion.span> : isReceiving ? <motion.span key="receiving" initial={{
                      opacity: 0
                    }} animate={{
                      opacity: 1
                    }} exit={{
                      opacity: 0
                    }} transition={{
                      duration: 0.15
                    }} className="text-accent" data-id="element-64">
                            Matching{' '}
                            {totalForDest > 1 ? `${totalForDest} files…` : '1 file…'}
                          </motion.span> : <motion.span key="awaiting" initial={{
                      opacity: 0
                    }} animate={{
                      opacity: 1
                    }} exit={{
                      opacity: 0
                    }} transition={{
                      duration: 0.15
                    }} className="text-zinc-600" data-id="element-65">
                            Awaiting{' '}
                            {totalForDest > 1 ? `${totalForDest} files` : '1 file'}
                          </motion.span>}
                      </AnimatePresence>
                    </div>
                  </div>

                  {/* Inline file chips — fly in from the right while receiving, settle when filed */}
                  <div className="flex flex-shrink-0 items-center -space-x-2" data-id="element-66">
                    <AnimatePresence data-id="element-67">
                      {(isReceiving || isFilled) && matchedAttachments.slice(0, 3).map((a, idx) => <motion.div key={a.name} initial={{
                    opacity: 0,
                    x: 20,
                    scale: 0.8
                  }} animate={{
                    opacity: 1,
                    x: 0,
                    scale: 1
                  }} exit={{
                    opacity: 0,
                    scale: 0.8
                  }} transition={{
                    duration: 0.45,
                    delay: isReceiving ? idx * 0.12 : 0,
                    ease: [0.22, 1, 0.36, 1]
                  }} className={`flex h-6 w-6 items-center justify-center rounded-md border ${isFilled ? 'border-emerald-500/30 bg-emerald-500/10' : 'border-accent/40 bg-accent/15'}`} style={{
                    zIndex: 10 - idx
                  }} data-id="element-68">
                            <FileIcon className={`h-3 w-3 ${isFilled ? 'text-emerald-400' : 'text-accent'}`} strokeWidth={2} data-id="element-69" />
                          </motion.div>)}
                    </AnimatePresence>
                    {totalForDest > 3 && (isReceiving || isFilled) && <motion.div initial={{
                  opacity: 0,
                  scale: 0.8
                }} animate={{
                  opacity: 1,
                  scale: 1
                }} transition={{
                  delay: 0.4,
                  duration: 0.3
                }} className={`flex h-6 min-w-[24px] items-center justify-center rounded-md border px-1 font-mono text-[9px] ${isFilled ? 'border-emerald-500/30 bg-emerald-500/10 text-emerald-400' : 'border-accent/40 bg-accent/15 text-accent'}`} style={{
                  zIndex: 5
                }} data-id="element-70">
                        +{totalForDest - 3}
                      </motion.div>}
                  </div>
                </div>
              </motion.li>;
        })}
        </ul>
      </div>

      {/* DESKTOP: original two-column wired layout */}
      <div className="relative hidden gap-4 sm:grid sm:grid-cols-2" data-id="element-71">
        {/* SVG wire connectors — circuit-board traces between sources and destinations */}
        <div ref={wiresRef} aria-hidden className="pointer-events-none absolute inset-0 z-10" data-id="element-72">
          <svg className="h-full w-full overflow-visible" data-id="element-73">
            <defs data-id="element-74">
              <linearGradient id="wire-active-gradient" x1="0%" y1="0%" x2="100%" y2="0%" data-id="element-75">
                <stop offset="0%" stopColor="#00F0FF" data-id="element-76" />
                <stop offset="50%" stopColor="#00F0FF" data-id="element-77" />
                <stop offset="100%" stopColor="#00F0FF" data-id="element-78" />
              </linearGradient>
              <filter id="wire-glow" x="-50%" y="-50%" width="200%" height="200%" data-id="element-79">
                <feGaussianBlur stdDeviation="10" result="blur" data-id="element-80" />
                <feMerge data-id="element-81">
                  <feMergeNode in="blur" data-id="element-82" />
                  <feMergeNode in="SourceGraphic" data-id="element-83" />
                </feMerge>
              </filter>
            </defs>
            {paths.map(p => {
            const isActive = checklist[p.destIdx]?.label === activeMapping;
            const isDone = p.destIdx < processedDestIdx && !isActive;
            const isVisible = isActive || isDone;
            return <g key={`wire-${p.sourceIdx}`} data-id="element-84">
                  {/* Faint base trace — always visible like a circuit trace */}
                  <motion.path d={p.d} fill="none" strokeLinecap="round" initial={false} animate={{
                stroke: isDone ? '#10b981' : '#38BDF8',
                strokeOpacity: isVisible ? 0.55 : isDone ? 0.32 : 0,
                strokeWidth: isVisible ? 1.75 : 1
              }} transition={{
                duration: 0.3
              }} pathLength={isVisible ? 1 : 0} data-id="element-85" />
                  {/* Traveling packet — only while this destination is receiving */}
                  <AnimatePresence data-id="element-86">
                    {isActive && <motion.path key={`packet-${p.sourceIdx}-${activeDestIdx}`} d={p.d} pathLength={1} fill="none" stroke="#38BDF8" strokeWidth={2.5} strokeLinecap="round" strokeDasharray="0.06 1.06" initial={{
                  strokeDashoffset: 0.06
                }} animate={{
                  strokeDashoffset: -1
                }} exit={{
                  opacity: 0
                }} transition={{
                  duration: 0.75,
                  ease: 'easeOut'
                }} style={{
                  filter: 'drop-shadow(0 0 4px rgba(56,189,248,0.9))'
                }} data-id="element-87" />}
                  </AnimatePresence>
                </g>;
          })}
          </svg>
        </div>
        {/* SOURCE — incoming attachments. All attachments mapped to the active destination pulse together. */}
        <div className="rounded-xl border border-white/5 bg-zinc-950/60 p-4" data-id="element-88">
          <div className="mb-3 text-xs font-medium uppercase tracking-wider text-zinc-500" data-id="element-89">
            Email Attachments
          </div>
          <ul className="space-y-1.5" data-id="element-90">
            {attachments.map((a, srcIdx) => {
            const destIdx = checklist.findIndex(c => c.label === a.mapped);
            const isActive = a.mapped === activeMapping;
            const isCategorized = destIdx >= 0 && destIdx < processedDestIdx;
            return <motion.li key={a.name} ref={el => {
              sourceRefs.current[srcIdx] = el;
            }} animate={{
              opacity: isCategorized ? 0.45 : 1,
              borderColor: isActive ? 'rgba(56,189,248,0.55)' : 'rgba(255,255,255,0.05)',
              backgroundColor: isActive ? 'rgba(56,189,248,0.10)' : 'rgba(24,24,27,0.6)'
            }} transition={{
              duration: 0.3
            }} className="relative flex items-center gap-2 rounded-md border px-2.5 py-2 text-xs" data-id="element-91">
                  {isActive && <motion.div aria-hidden animate={{
                opacity: [0, 0.7, 0]
              }} transition={{
                duration: 1,
                repeat: Infinity
              }} className="pointer-events-none absolute inset-0 rounded-md ring-1 ring-accent/60" data-id="element-92" />}
                  <FileIcon className={`h-3.5 w-3.5 flex-shrink-0 transition-colors ${isActive ? 'text-accent' : 'text-zinc-500'}`} data-id="element-93" />
                  <span className="flex-1 truncate text-zinc-300" data-id="element-94">
                    {a.name}
                  </span>
                  <AnimatePresence mode="wait" data-id="element-95">
                    {isCategorized ? <motion.div key="done" initial={{
                  scale: 0,
                  opacity: 0
                }} animate={{
                  scale: 1,
                  opacity: 1
                }} exit={{
                  scale: 0,
                  opacity: 0
                }} transition={{
                  duration: 0.25,
                  ease: 'backOut'
                }} className="flex h-3.5 w-3.5 items-center justify-center rounded-full bg-accent/20" data-id="element-96">
                        <CheckIcon className="h-2 w-2 text-accent" strokeWidth={3.5} data-id="element-97" />
                      </motion.div> : <motion.span key="size" initial={{
                  opacity: 0
                }} animate={{
                  opacity: 1
                }} exit={{
                  opacity: 0
                }} transition={{
                  duration: 0.15
                }} className="font-mono text-[10px] text-zinc-600" data-id="element-98">
                        {a.size}
                      </motion.span>}
                  </AnimatePresence>
                </motion.li>;
          })}
          </ul>
        </div>

        {/* DESTINATION — checklist. Active dest receives, prior dests are filled. */}
        <div className="flex flex-col rounded-xl border border-white/5 bg-zinc-950/60 p-4" data-id="element-99">
          <div className="mb-3 text-xs font-medium uppercase tracking-wider text-zinc-500" data-id="element-100">
            Request Checklist
          </div>
          <ul className="flex flex-1 flex-col gap-1.5" data-id="element-101">
            {checklist.map((c, i) => {
            const totalForDest = attachments.filter(a => a.mapped === c.label).length;
            const isFilled = i < processedDestIdx;
            const isReceiving = i === activeDestIdx;
            const matchCount = isFilled ? totalForDest : 0;
            return <motion.li key={c.label} ref={el => {
              destRefs.current[i] = el;
            }} animate={{
              borderColor: isFilled ? 'rgba(16,185,129,0.30)' : isReceiving ? 'rgba(56,189,248,0.55)' : 'rgba(255,255,255,0.05)',
              backgroundColor: isFilled ? 'rgba(16,185,129,0.05)' : isReceiving ? 'rgba(56,189,248,0.10)' : 'rgba(24,24,27,0.4)'
            }} transition={{
              duration: 0.3
            }} className="relative flex flex-1 items-center gap-3 rounded-md border px-3 text-xs" data-id="element-102">
                  {isReceiving && <motion.div aria-hidden animate={{
                opacity: [0, 0.7, 0]
              }} transition={{
                duration: 1,
                repeat: Infinity
              }} className="pointer-events-none absolute inset-0 rounded-md ring-1 ring-accent/60" data-id="element-103" />}
                  <div className={`flex h-6 w-6 flex-shrink-0 items-center justify-center rounded-full transition-colors ${isFilled ? 'bg-emerald-500/20' : isReceiving ? 'bg-accent/15' : 'border border-white/10 bg-white/[0.03]'}`} data-id="element-104">
                    <AnimatePresence mode="wait" data-id="element-105">
                      {isFilled ? <motion.div key="check" initial={{
                    scale: 0,
                    opacity: 0
                  }} animate={{
                    scale: 1,
                    opacity: 1
                  }} exit={{
                    scale: 0,
                    opacity: 0
                  }} transition={{
                    duration: 0.3,
                    ease: 'backOut'
                  }} data-id="element-106">
                          <CheckIcon className="h-3.5 w-3.5 text-emerald-400" strokeWidth={3} data-id="element-107" />
                        </motion.div> : isReceiving ? <motion.div key="spinner" initial={{
                    opacity: 0
                  }} animate={{
                    opacity: 1
                  }} exit={{
                    opacity: 0
                  }} transition={{
                    duration: 0.15
                  }} data-id="element-108">
                          <motion.div animate={{
                      rotate: 360
                    }} transition={{
                      duration: 1.2,
                      repeat: Infinity,
                      ease: 'linear'
                    }} className="h-4 w-4 rounded-full border-2 border-accent/20 border-t-accent" data-id="element-109" />
                        </motion.div> : null}
                    </AnimatePresence>
                  </div>
                  <div className="min-w-0 flex-1" data-id="element-110">
                    <div className={`leading-tight transition-colors ${isFilled ? 'text-zinc-200' : 'text-zinc-500'}`} data-id="element-111">
                      {c.short}
                    </div>
                    <div className="mt-1 font-mono text-[9px] uppercase tracking-[0.16em] text-zinc-600" data-id="element-112">
                      <AnimatePresence mode="wait" data-id="element-113">
                        {isFilled ? <motion.span key="filed" initial={{
                      opacity: 0,
                      y: 2
                    }} animate={{
                      opacity: 1,
                      y: 0
                    }} exit={{
                      opacity: 0
                    }} transition={{
                      duration: 0.2
                    }} className="text-emerald-400" data-id="element-114">
                            {totalForDest > 1 ? `${matchCount} files filed` : '1 file filed'}
                          </motion.span> : isReceiving ? <motion.span key="receiving" initial={{
                      opacity: 0
                    }} animate={{
                      opacity: 1
                    }} exit={{
                      opacity: 0
                    }} transition={{
                      duration: 0.15
                    }} className="text-accent" data-id="element-115">
                            Matching{' '}
                            {totalForDest > 1 ? `${totalForDest} files…` : '1 file…'}
                          </motion.span> : <motion.span key="awaiting" initial={{
                      opacity: 0
                    }} animate={{
                      opacity: 1
                    }} exit={{
                      opacity: 0
                    }} transition={{
                      duration: 0.15
                    }} data-id="element-116">
                            Awaiting{' '}
                            {totalForDest > 1 ? `${totalForDest} files` : '1 file'}
                          </motion.span>}
                      </AnimatePresence>
                    </div>
                  </div>
                </motion.li>;
          })}
          </ul>
        </div>
      </div>

      <div className="mt-4 text-center text-[11px] text-zinc-500" data-id="element-117">
        Any file type. No restrictions.
      </div>
    </div>;
}